import { isTrueDemandInventory } from '@epicuro-next/state/config-strip-state';
import {
  isAfter,
  isBefore,
  startOfTheDayDate,
  stringDatesEqual,
} from '@epicuro-next/utilities';
import { OrderType } from 'graphql-api/gql-api-interfaces';
import { pipe } from 'rxjs';

import {
  DEFAULT_DELIVERY_PERIOD_ID,
  DeliveryItem,
  DeliveryItems,
  ProductPrepackEntities,
  ProductSizeEntities,
} from '../../models/config-strip-state.models';
import { getNowDateContainer } from '../../shared/now-date-container/get-now-date-container';

import {
  availableInventoriesForSelectedSizeForOrderType,
  availableInventoriesQuantitiesForDeliveryPeriod,
} from './inventory-overview';
import { RequestedQuantity } from './requested-quantity';
import { spreadSelectedQuantityOverInventories } from './spread-selected-quantity-over-inventories';

const handleFutureRequest = (
  deliveryPeriod: string,
  deliveryItem: DeliveryItem,
) => {
  const { useInventoryDate, availabilityDateFrom, availabilityDateTo } =
    deliveryItem;
  // Based on displayFromDateUtc. If it's set we always have to send
  // availableDateFrom. displayFromDateUtc is used for pre-calculating tiles, for instance
  if (useInventoryDate) {
    return {
      requestedDeliveryDate: availabilityDateFrom ?? deliveryPeriod,
    };
  }

  // adjusting deliveryPeriod forward
  if (availabilityDateFrom) {
    // we use first day of week for week delivery periods
    // this checks if first availability is in the middle of the week, and replaces period date if true
    deliveryPeriod = isBefore(
      new Date(availabilityDateFrom),
      new Date(deliveryPeriod),
    )
      ? deliveryPeriod
      : availabilityDateFrom;

    // deliveryPeriod and availabilityDateFrom is the same, we have to set timestamp
    // as in availabilityDateFrom so we follow the same logic of SDO/MDO upload
    // https://epicuro.atlassian.net/wiki/spaces/PD/pages/3108864045/Config%2BStrip%2Band%2BSDO%2BMDO%2Binterfaces%2Bmismatch
    if (
      stringDatesEqual(
        startOfTheDayDate(deliveryPeriod),
        startOfTheDayDate(availabilityDateFrom),
      )
    ) {
      deliveryPeriod = availabilityDateFrom;
    }
  }

  // adjusting deliveryPeriod backward
  if (availabilityDateTo) {
    // we use first day of week for week delivery periods
    // this checks if first availability is in the middle of the week, and replaces period date if true
    deliveryPeriod = isAfter(
      new Date(deliveryPeriod),
      new Date(availabilityDateTo),
    )
      ? availabilityDateTo
      : deliveryPeriod;
  }

  return {
    requestedDeliveryDate: deliveryPeriod,
  };
};

// NB: this function places pre-packs in today date for Now order
// and return its quantity as total of pre-packs (not total of items in it)
// Check usage of this function in now-totals.selector
// to see how total of pre-packs is converted to the total of items in it
export const splitQuantities = (
  productId: string,
  deliveryItems: DeliveryItems,
  deliveryPeriod: string,
  sizes: ProductSizeEntities,
  prepacks: ProductPrepackEntities,
  splitNowQuantity = false,
  skipInventoryCheck = false,
): RequestedQuantity[] => {
  const isNow = deliveryPeriod === DEFAULT_DELIVERY_PERIOD_ID;
  const requestedQuantity =
    deliveryItems.items[productId].quantity -
    (deliveryItems.items[productId].usedTrueDemandQuantity ?? 0);
  const isPrepack = Object.keys(prepacks).includes(productId);

  const inventories = isPrepack
    ? prepacks[productId]?.inventoryItems
    : sizes[productId]?.inventoryItems;

  if (!inventories || skipInventoryCheck) {
    if (!isNow) {
      return [
        {
          ...handleFutureRequest(
            deliveryPeriod,
            deliveryItems.items[productId],
          ),
          requestedQuantity,
        },
      ];
    }
    return [
      {
        requestedDeliveryDate: getNowDateContainer(),
        requestedQuantity,
      },
    ];
  }

  const inventoryOverviews = pipe(
    availableInventoriesForSelectedSizeForOrderType,
    availableInventoriesQuantitiesForDeliveryPeriod,
  )(
    inventories.filter((item) =>
      isNow
        ? item.orderType === OrderType.Now
        : item.orderType !== OrderType.Now,
    ),
  );

  const trueDemandAvailableOverview =
    deliveryItems.items[productId].usedTrueDemandQuantity > 0
      ? inventoryOverviews.find((i) => isTrueDemandInventory(i.inventoryId))
      : null;

  // we provide available bulk as 0 to not treat bulk as a separate inventory, it all goes as one basket item
  const requestedQuantities = spreadSelectedQuantityOverInventories(
    requestedQuantity,
    0,
    inventoryOverviews.filter((i) => !isTrueDemandInventory(i.inventoryId)),
    skipInventoryCheck,
  ).map((quantity, index) => {
    if (!isNow) {
      return {
        ...handleFutureRequest(deliveryPeriod, deliveryItems.items[productId]),
        requestedQuantity: quantity,
        inventoryId: inventoryOverviews[index].inventoryId,
      };
    }
    if (!splitNowQuantity || isPrepack) {
      return {
        requestedDeliveryDate: getNowDateContainer(),
        requestedQuantity: quantity,
        inventoryId: inventoryOverviews[index].inventoryId,
      };
    }
    return {
      requestedDeliveryDate: inventoryOverviews[index].deliveryPeriod,
      requestedQuantity: quantity,
      inventoryId: inventoryOverviews[index].inventoryId,
    };
  });

  const requestedTrueDemandQuantities = trueDemandAvailableOverview
    ? [
        {
          requestedDeliveryDate: getNowDateContainer(),
          requestedQuantity:
            deliveryItems.items[productId].usedTrueDemandQuantity,
          inventoryId: trueDemandAvailableOverview.inventoryId,
        },
      ]
    : [];

  return [...requestedQuantities, ...requestedTrueDemandQuantities];

  // output of this function produces empty quantities for inventories that were not consumed
  // it helps to cleanup back order and it seems GQL can handle 0 quantities well

  // however, 0 qty supposed to indicate that we want to remove certain basket item,
  // and the point that we send 0 qty for non existing basket item looks odd
  // in case of troubles, we should filter items by requestedQuantity

  // ->> .filter((item) => item.requestedQuantity > 0);

  // and cover case of removing back order in a smarter way
  // case to cover: (17 + 13) = 30 items ordered for now, but split into 2 dates - 30.03 and 1.04
  // user changes it to 15, so basket item of 1.04 should be removed and other basket item changes qty to 15 = (15 + 0)
};
